import React, { useEffect, useState, useRef } from "react";
import "../../assets/css/Services.css";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

// meta data
import { Helmet } from "react-helmet";
import data from "../../component/ApiUrl/meta_api";

/* ─── Data ─────────────────────────────────────────────────────────────────── */
const cards = [
  {
    icon: "fa-solid fa-microchip",
    title: "Embedded Systems Design & Development",
    para: "We design and develop robust embedded systems from concept to production — delivering reliable firmware, hardware, and microcontroller-based solutions for real-world applications.",
    list: [
      "Firmware & Microcontroller Programming",
      "ARM & RTOS Development",
      "PCB Design & Prototyping",
      "Prototype to Production Support",
    ],
  },

  {
    icon: "fa-solid fa-network-wired",
    title: "IIoT Connectivity & Integration",
    para: "Connect your industrial devices, machines, and systems seamlessly to the cloud with our end-to-end IIoT integration services built for smart manufacturing environments.",
    list: [
      "Device-to-Cloud Connectivity",
      "MQTT & OPC-UA Protocol Integration",
      "Gateway & Edge Device Setup",
      "Cloud Platform Integration (AWS/Azure)",
    ],
  },

  {
    icon: "fa-solid fa-chart-line",
    title: "Smart Monitoring & Predictive Analytics",
    para: "Transform raw industrial data into actionable insights with our smart monitoring dashboards and AI-powered predictive analytics for proactive maintenance and decision-making.",
    list: [
      "Real-Time Dashboard Development",
      "Predictive Maintenance Systems",
      "AI-Driven Fault Detection",
      "Machine Health & KPI Tracking",
    ],
  },

  {
    icon: "fa-solid fa-industry",
    title: "Industrial Automation & Control Systems",
    para: "Build smarter factories with our integrated industrial automation and control solutions combining PLC, SCADA, HMI, and robotics for a fully connected production environment.",
    list: [
      "PLC Programming & Integration",
      "HMI Design & Development",
      "Smart Factory & Industry 4.0",
      "System Commissioning & Support",
    ],
  },

  {
    icon: "fa-solid fa-wifi",
    title: "Wireless Sensor & Network Solutions",
    para: "Deploy intelligent wireless sensor networks across your facility to enable seamless communication, remote monitoring, and real-time data collection without complex wiring.",
    list: [
      "Wireless Sensor Networks (WSN)",
      "Modbus & Profibus Integration",
      "Sensor Interface & Calibration",
      "Remote Asset Monitoring",
    ],
  },

  {
    icon: "fa-solid fa-cube",
    title: "Digital Twin & Energy Optimization",
    para: "Create virtual replicas of your physical assets and optimize energy usage with our digital twin technology enabling simulation, testing, and continuous performance improvement.",
    list: [
      "Digital Twin Implementation",
      "Energy Monitoring & Optimization",
      "Simulation & Virtual Testing",
      "Continuous Performance Improvement",
    ],
  },
];

const STEPS = [
  {
    icon: "fa-magnifying-glass",
    title: "Requirement Analysis & System Planning",
    desc: "Analyze industrial processes and machines to identify automation and IIoT opportunities.",
  },
  {
    icon: "fa-lightbulb",
    title: "Embedded System & Architecture Design",
    desc: "Design scalable embedded systems, PCB, firmware, and IIoT architecture.",
  },
  {
    icon: "fa-microchip",
    title: "Firmware Development & Integration",
    desc: "Develop firmware and integrate systems with sensors, PLC, SCADA, networks.",
  },
  {
    icon: "fa-network-wired",
    title: "IIoT Deployment & Cloud Integration",
    desc: "Enable device connectivity, real-time data transfer, and cloud integration.",
  },
  {
    icon: "fa-gauge-high",
    title: "Monitoring, Analytics & Optimization",
    desc: "Implement dashboards, predictive maintenance, and analytics to improve system efficiency.",
  },
];

const FAQ_DATA = [
  [
    "What Embedded Systems and IIoT services do you offer?",
    "We provide complete Embedded Systems Development and Industrial IoT (IIoT) solutions including PCB design, firmware development, sensor integration, industrial automation, cloud connectivity, and real-time monitoring systems for smart industries.",
  ],
  [
    "How can IIoT solutions improve my business operations?",
    "IIoT solutions enable real-time data monitoring, predictive maintenance, and automation of industrial processes. This helps reduce downtime, improve efficiency, and optimize overall productivity in manufacturing and industrial environments.",
  ],
  [
    "Do you develop custom embedded systems?",
    "Yes, we design and develop custom embedded systems tailored to your specific industrial requirements, including microcontroller-based systems, IoT devices, and real-time control applications.",
  ],
  [
    "What industries do you support with Embedded Systems and IIoT?",
    "We serve industries like manufacturing, energy, agriculture, automotive, healthcare, and smart infrastructure by providing industrial automation and IoT-based smart solutions.",
  ],
  [
    "Do you provide PCB design and firmware development?",
    "Yes, we offer end-to-end services including PCB design, circuit development, embedded firmware programming, and hardware-software integration for industrial and IoT applications.",
  ],
  [
    "What is included in your Industrial Automation solutions?",
    "Our industrial automation services include PLC programming, SCADA integration, sensor connectivity, machine automation, and system optimization to improve operational efficiency.",
  ],
  [
    "How long does it take to develop an embedded system or IIoT solution?",
    "The development timeline depends on project complexity. Basic embedded systems may take a few weeks, while advanced IIoT and industrial automation solutions can take several months including design, development, and deployment.",
  ],
  [
    "Do you provide real-time monitoring and analytics?",
    "Yes, we develop real-time monitoring dashboards with data analytics, enabling businesses to track machine performance, energy usage, and system health remotely.",
  ],
  [
    "Are your IIoT systems secure and scalable?",
    "Yes, we build secure and scalable IIoT systems using industry-standard protocols, encrypted communication, and cloud-based architecture to ensure data safety and future expansion.",
  ],
  [
    "How can I get started with your Embedded Systems and IIoT services?",
    "You can contact us for a free consultation. We will analyze your requirements, suggest the best embedded and IIoT solutions, and provide a customized development plan.",
  ],
];

/* ─── Small components ──────────────────────────────────────────────────────── */
function SectionLabel({ text }) {
  return (
    <p
      className="fw-semibold text-uppercase mb-1"
      style={{ fontSize: 20, color: "#cc0000", letterSpacing: 1 }}
    >
      {text}
    </p>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      className={`rs-faq-item rs-anim${open ? " open" : ""}`}
      style={{ transitionDelay: `${idx * 55}ms` }}
    >
      <button className="rs-faq-btn" onClick={() => setOpen((o) => !o)}>
        <span className="rs-faq-q">{q}</span>
        <span className="rs-faq-icon">
          <i className="fa-solid fa-plus" />
        </span>
      </button>
      <div className="rs-faq-body">{a}</div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function EmbaddedSystem() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);

  // scroll top
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const fetchData = () => {
    const EmbaddedSystemMeta = data[0]?.embedded_systems_page;
    if (EmbaddedSystemMeta) {
      setMeta(EmbaddedSystemMeta);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: "ease-in-out",
    });

    if (!document.getElementById("rs-styles")) {
      const s = document.createElement("style");
      s.id = "rs-styles";
      document.head.appendChild(s);
    }
    if (!document.querySelector('link[href*="font-awesome"]')) {
      const fa = document.createElement("link");
      fa.rel = "stylesheet";
      fa.href =
        "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css";
      document.head.appendChild(fa);
    }
    if (!document.querySelector('link[href*="Poppins"]')) {
      const gf = document.createElement("link");
      gf.rel = "stylesheet";
      gf.href =
        "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap";
      document.head.appendChild(gf);
    }
  }, []);

  return (
    <div className="body">
      <Helmet>
        <title>{meta ? meta.title : "Embedded Systems"}</title>
      </Helmet>
      {/* ═════════
          1. HERO 
      ═════════════ */}
      <div
        className="backgroundCommenImage position-relative d-flex align-items-center mb-5"
        style={{
          backgroundImage: `url(${require("../../assets/img/Service/Embadded Sytem Service/banner.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "60vh",
        }}
      >
        {/* Overlay */}
        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{ background: "rgba(0, 0, 0, 0.75)", zIndex: 1 }}
        ></div>

        {/* Content */}
        <div
          className="container position-relative text-center text-white py-4 py-md-5"
          style={{ zIndex: 2 }}
        >
          {/* Badge */}
          <span
            className="fw-semibold d-inline-block mb-3 px-3 py-1 rounded-pill"
            style={{
              background: "#cc0000",
              fontSize: "clamp(10px,3vw,12px)",
              letterSpacing: "0.5px",
            }}
            data-aos="fade-down"
            data-aos-delay="100"
          >
            ✦ Embedded Systems · IIoT · PLC · SCADA · Industrial Automation
          </span>

          {/* Heading */}
          <h1
            className="fw-semibold mb-3"
            style={{
              fontSize: "clamp(18px,5vw,28px)",
              lineHeight: 1.4,
            }}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Embedded Systems & IIoT Services
            <br />
            <span style={{ color: "#cc0000" }}>
              Smart Manufacturing · PCB Design · Firmware · Industry 4.0
              Solutions
            </span>
          </h1>

          {/* Text */}
          <p
            className="mx-auto mb-4 px-2 px-md-0"
            style={{
              fontSize: "clamp(13px,3.5vw,15px)",
              maxWidth: "650px",
              lineHeight: 1.7,
              color: "#ebe6e6",
            }}
            data-aos="fade-up"
            data-aos-delay="350"
          >
            End-to-end Embedded Systems & IIoT services: hardware design, PCB,
            firmware, automation, smart manufacturing, real-time monitoring,
            predictive maintenance.
          </p>
        </div>
      </div>

      {/* ════════════
          2. INTRO — 
      ═════════════════ */}
      <div className="container margin_top container_width">
        <div className="row align-items-center justify-content-center g-5">
          {/* Left */}
          <div className="col-lg-6" data-aos="fade-right" data-aos-delay="100">
            <SectionLabel text="Embedded Systems & IIoT Services" />

            <h2 className="fw-semibold mb-3" style={{ fontSize: 20 }}>
              Advanced Embedded Systems & IIoT Solutions for Smart Industries
            </h2>

            <p className="mb-3 content-text">
              <span className="fw-semibold">Skimitar Hindustan Pvt Ltd</span>{" "}
              provides advanced Embedded Systems and Industrial IoT (IIoT)
              services to build intelligent and connected industrial solutions.
              Our expertise includes embedded hardware design, PCB development,
              firmware programming, and IIoT integration to enable real-time
              monitoring, smart automation, and efficient manufacturing
              operations.
            </p>

            <ul className="list-unstyled mt-3">
              {[
                "Custom Embedded Systems Development for industrial automation and IIoT applications",
                "Industrial-grade PCB Design and Prototyping for harsh environments",
                "Firmware & RTOS Development for real-time control and processing",
                "IIoT Integration for machine connectivity and real-time monitoring",
                "Predictive Maintenance solutions to reduce downtime and improve efficiency",
                "Scalable and reliable systems for smart manufacturing and Industry 4.0",
              ].map((item, i) => (
                <li
                  key={i}
                  className="d-flex align-items-start gap-2 mb-2 small"
                  data-aos="fade-up"
                  data-aos-delay={`${100 + i * 80}`}
                >
                  <i className="fa-solid fa-check text-danger mt-1"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right */}
          <div
            className="col-lg-6 text-center"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            <div className="position-relative d-inline-block w-100">
              {/* Image */}
              <img
                className="img-fluid rounded-4"
                src={require("../../assets/img/Service/Embadded Sytem Service/img-1.jpg")}
                alt="Embedded Systems and IIoT Services"
              />

              {/* Stat */}
              <div className="retro-stat">
                <i className="fa-solid fa-industry text-red fs-5"></i>
                <div>
                  <div className="fw-bold fs-5">150+</div>
                  <div className="small text-muted">
                    Embedded & IIoT Projects Delivered
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          3. SERVICE CARDS
      ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top pb-5">
        <div className="container container_width">
          <div className="mb-1" data-aos="fade-up">
            <SectionLabel text="Our Embedded Systems & IIoT Services" />
          </div>

          <div className="row mb-4">
            <div className="col-lg-7" data-aos="fade-up" data-aos-delay="100">
              <h2 className="fw-semibold mb-2" style={{ fontSize: 20 }}>
                Advanced Embedded Systems & IIoT Solutions for Smart Industries
              </h2>

              <p className="mb-3 content-text">
                From embedded hardware design to Industrial IoT (IIoT)
                integration, we deliver end-to-end solutions that enable smart
                manufacturing, real-time monitoring, and data-driven industrial
                automation.
              </p>
            </div>

            <div
              className="col-lg-5 text-lg-end mt-3 mt-lg-0"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <a href="#contact" className="btn btn-red px-4 py-2">
                Get Embedded System
                <i className="fa-solid fa-arrow-right ms-2"></i>
              </a>
            </div>
          </div>

          {/* Content */}
          <div className="row g-3">
            {cards.map((s, i) => (
              <div
                key={i}
                className="col-12 col-sm-6 col-lg-4"
                data-aos="fade-up"
                data-aos-delay={`${i * 100}`}
              >
                <div className="card h-100 border-0 shadow service-card">
                  <div className="card-body position-relative p-4">
                    {/* Number */}
                    {/* <span className="service-number">
                      {String(i + 1).padStart(2, "0")}
                    </span> */}

                    {/* Icon */}
                    <div className="service-icon mb-3">
                      <i className={`fa-solid ${s.icon}`}></i>
                    </div>

                    {/* title */}
                    <h5
                      className="fw-bold mb-2 text-red"
                      style={{ fontSize: 15 }}
                    >
                      {s.title}
                    </h5>

                    {/* para */}
                    <p className="small text-muted mb-2">{s.para}</p>

                    {/* list */}
                    <ul className="list-unstyled">
                      {s.list.map((item, i) => (
                        <li
                          key={i}
                          className="d-flex align-items-start gap-2 mb-2 small"
                          data-aos="fade-up"
                          data-aos-delay={`${100 + i * 80}`}
                        >
                          <i className="fa-solid fa-check text-danger mt-1"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <button className="btn btn-red btn-sm mt-2">
                      Enquire Now
                      <i className="fa-solid fa-arrow-right ms-2"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          4. HOW IT WORKS — 5-step process
      ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top py-5 bg-light" id="process">
        <div className="container container_width">
          <div className="mb-2" data-aos="fade-up">
            <SectionLabel text="How It Works" />
          </div>

          <div className="mb-5" data-aos="fade-up" data-aos-delay="100">
            <h2 className="fw-semibold mb-3" style={{ fontSize: 20 }}>
              Our Embedded Systems & IIoT Development Workflow
            </h2>

            <p className="mb-3 content-text">
              We follow a structured Embedded Systems and Industrial IoT (IIoT)
              development process to deliver reliable and scalable solutions.
              Our approach ensures seamless integration of hardware, firmware,
              and connected systems, enabling real-time monitoring, smart
              automation, and improved operational efficiency.
            </p>
          </div>

          <div className="row g-4 text-center justify-content-center">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className="col-12 col-md-4 col-lg position-relative"
                data-aos="fade-up"
                data-aos-delay={`${i * 120}`}
              >
                {/* connector */}
                {i < STEPS.length - 1 && (
                  <div className="process-line d-none d-lg-block"></div>
                )}

                {/* circle */}
                <div className="process-circle mx-auto mb-3 d-flex align-items-center justify-content-center position-relative">
                  <i className={`fa-solid ${step.icon} text-red fs-5`}></i>

                  <span className="badge bg-red position-absolute process-number">
                    {i + 1}
                  </span>
                </div>

                <h5 className="fw-bold small mb-1">{step.title}</h5>

                <p className="text-muted small mb-0">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          5. FAQ
      ══════════════════════════════════════════════════════════════ */}
      <section className="margin_top pb-5 rs-bg-alt" id="faq">
        <div className="container container_width py-5">
          <div className="text-center rs-anim mb-1" data-aos="fade-up">
            <SectionLabel text="FAQ" />
          </div>
          <div
            className="text-center rs-anim mb-5"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="fw-semibold" style={{ fontSize: 20 }}>
              Embedded Systems & IIoT – Frequently Asked Questions
            </h2>
            <p className="mb-3 content-text">
              Everything you need to know about our Embedded Systems
              Development, Industrial IoT (IIoT) Solutions, Industrial
              Automation, PCB Design, Firmware Development, and Smart
              Manufacturing services.
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-9">
              {FAQ_DATA.map(([q, a], i) => (
                <FAQItem key={i} q={q} a={a} idx={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          6. GET IN TOUCH — CTA
      ══════════════════════════════════════════════════════════════ */}
      <section id="contact">
        <div className="p-5" style={{ background: "#cc0000" }}>
          <div
            className="d-flex align-items-center justify-content-center gap-3 mb-3"
            data-aos="fade-down"
          >
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
            <span
              className="text-uppercase fw-semibold text-white text-opacity-75"
              style={{
                letterSpacing: "2px",
                whiteSpace: "nowrap",
                fontSize: "13px",
              }}
            >
              Transform Your Industry with IIoT
            </span>
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
          </div>

          <div className="row g-4 align-items-center">
            <div
              className="col-lg-7 text-center text-lg-start"
              data-aos="fade-right"
              data-aos-delay="100"
            >
              <h2 className="fw-bolder text-white text-uppercase mb-3">
                Build Smart Systems with <br />
                Embedded & IIoT Experts
              </h2>

              <p
                className="text-white text-opacity-75 mb-0"
                style={{ lineHeight: "1.75" }}
              >
                Upgrade your business with advanced Embedded Systems and
                Industrial IoT (IIoT) solutions. We help industries implement
                automation, real-time monitoring, predictive maintenance, and
                smart manufacturing systems using cutting-edge technologies, PCB
                design, and firmware development.
              </p>
            </div>

            <div
              className="col-lg-5"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-end">
                <button
                  className="btn oc-btn-white px-4 py-3 fw-bold text-uppercase"
                  onClick={() => navigate("/contact")}
                >
                  Get Free Consultation →
                </button>

                <a
                  className="btn oc-btn-ghost px-4 py-3 fw-bold text-uppercase"
                  href="tel:8428010909"
                >
                  📞 (+91) 8428010909
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
